import { cn } from '@/lib/utils';
import { cva, type VariantProps } from 'class-variance-authority';
import clsx from 'clsx';
import { LoaderCircle } from 'lucide-react';
import { Slot } from 'radix-ui';
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { forwardRef } from 'react';

const buttonVariants = cva(
  clsx(
    'inline-flex items-center justify-center gap-2',
    '[&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0', // ?? From Change log of button @ ShadCN
    'cursor-pointer',
    'text-gray-50',
    'shadow-sm',
    'transition-colors',
    // focus
    // 'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-gray-950',
    // disabled
    'disabled:pointer-events-none',
    // 'disabled:opacity-60',
    // dark
    'dark:focus-visible:ring-gray-300',
    // text
    'whitespace-nowrap text-sm font-medium',
    // border radius
    'rounded-md',
  ),
  {
    variants: {
      variant: {
        default: clsx(
          // Light / default
          'bg-primary',
          'hover:bg-primary-600',
          'active:bg-primary-700',
          // disabled
          'disabled:bg-gray-100',
          'disabled:text-gray-500',
          'disabled:border-gray-200',
          // TODO: Dark
          'dark:text-white',
          'dark:bg-primary-dark dark:hover:bg-primary',
        ),
        app: clsx(
          // Light / default
          'bg-primary-app',
          'hover:bg-primary-600',
          'active:bg-primary-700',
          // disabled
          'disabled:bg-primary-100',
          'disabled:text-primary-400',
          // TODO: Dark
          'dark:text-white',
          'dark:bg-primary-dark dark:hover:bg-primary',
        ),
        outline: clsx(
          // light/default
          'text-primary-400',
          'border border-solid border-primary-400',
          // hover:light/default
          'hover:text-primary-500',
          'hover:border-primary-400',
          // active:light/default
          'active:text-primary-700',
          'active:border-primary-700',
          // disabled

          'disabled:bg-gray-100',
          'disabled:text-gray-500',
          'disabled:border-gray-200',
          // TODO: Dark
          'dark:text-white',
          'dark:bg-primary-dark dark:hover:bg-primary',
        ),
        'outline-borderless': clsx(
          'text-primary-400',
          'hover:text-primary-500',
          'active:text-primary-700',
          // disabled
          'disabled:border-gray-200 disabled:text-gray-200',
          // TODO: Dark
          'dark:text-white',
          'dark:bg-primary-dark dark:hover:bg-primary',
        ),
        destructive: clsx(
          // light/default
          'bg-danger-400',
          'hover:bg-danger-600',
          'active:bg-danger-700',
          // hover:light/default
          'hover:bg-danger-500',
          // disabled
          'disabled:bg-danger-100',
          'disabled:text-danger-400',
          // TODO: Dark
          'dark:text-white',
          'dark:bg-danger-900 dark:hover:bg-danger',
        ),
        'destructive-outline': clsx(
          // light/default
          'text-danger-400',
          'border border-solid border-danger-400',
          // hover:light/default
          'hover:text-danger-500',
          'hover:border-danger-400',
          // active:light/default
          'active:text-danger-700',
          'active:border-danger-700',
          // disabled
          'disabled:border-danger-200 disabled:text-danger-200',
          // TODO: Dark
          'dark:text-white',
          'dark:bg-danger-dark dark:hover:bg-danger',
        ),
        success: clsx(
          // light/default
          'bg-success-500',
          'hover:bg-success-600',
          'active:bg-success-700',
          // disabled
          'disabled:bg-success-100',
          'disabled:text-success-400',
          // TODO: Dark
          'dark:text-white',
          'dark:bg-success-900 dark:hover:bg-success',
        ),
        info: clsx(
          // light/default
          'bg-info-500',
          'hover:bg-info-600',
          'active:bg-info-700',
          // disabled
          'disabled:bg-info-100',
          'disabled:text-info-400',
          // TODO: Dark
          'dark:text-white',
          'dark:bg-info-900 dark:hover:bg-info',
        ),
        warning: clsx(
          // light/default
          'bg-warning-500',
          'hover:bg-warning-600',
          'active:bg-warning-700',
          // disabled
          'disabled:bg-warning-100',
          'disabled:text-warning-400',
          // TODO: Dark
          'dark:text-white',
          'dark:bg-warning-900 dark:hover:bg-warning',
        ),
        secondary: clsx(
          // light/default
          'bg-gray-100',
          'text-gray-500',
          'hover:bg-gray-200',
          'active:bg-gray-300',
          // disabled
          'disabled:bg-gray-100',
          'disabled:text-gray-500',
          'disabled:border-gray-200',
          // TODO: Dark
          'dark:bg-gray-800',
          'dark:text-gray-50',
          'dark:hover:bg-gray-800',
          'dark:hover:bg-info',
        ),
        ghost: clsx(
          'text-gray-700',
          'active:bg-gray-300',
          'shadow-none',
          'hover:bg-gray-100 hover:text-gray-900 dark:hover:bg-gray-800 dark:hover:text-gray-50',
        ),
        link: 'text-gray-900 underline-offset-4 hover:underline dark:text-gray-50',
      },
      size: {
        xs: 'h-5 rounded-md px-2 text-xs',
        sm: 'h-6 rounded-md px-3 py-1 text-sm',
        default: 'h-7 px-4 py-1',
        lg: 'h-8 rounded-md px-8',
        xl: 'h-9 rounded-md px-8',
        icon: 'h-9 w-9',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export type THubertButtonVariants =
  | 'default'
  | 'app'
  | 'outline'
  | 'outline-borderless'
  | 'destructive'
  | 'destructive-outline'
  | 'success'
  | 'info'
  | 'secondary'
  | 'ghost'
  | 'link'
  | 'warning'
  | null
  | undefined;
export type THubertButtonSizes = 'default' | 'xs' | 'sm' | 'lg' | 'icon' | null | undefined;

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  loading?: boolean;
  icon?: ReactNode;
  iconLeft?: ReactNode;
  asChild?: boolean;
  contentClassName?: string;
}

const HubertButton = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      children,
      asChild = false,
      loading = false,
      icon,
      iconLeft,
      contentClassName,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot.Root : 'button';
    return (
      <Comp
        className={cn(
          buttonVariants({ variant, size, className }),
          !children && icon && 'block gap-0', // block instead of inline-flex for icon only button
          props.disabled && 'cursor-not-allowed opacity-50 hover:cursor-not-allowed',
        )}
        ref={ref}
        {...props}
        disabled={loading ? true : props.disabled}
      >
        {loading && (
          <span>
            <LoaderCircle className="mr-2 animate-spin" />
          </span>
        )}

        {iconLeft && !loading ? <span className={cn(children ? 'mr-1' : 'block')}>{iconLeft}</span> : null}

        <span className={cn(contentClassName)}>{children}</span>

        {icon && !loading ? <span className={cn(children ? 'ml-1' : 'block')}>{icon}</span> : null}
      </Comp>
    );
  },
);
HubertButton.displayName = 'HubertButton';

export { HubertButton };
